import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { RCardGet } from '../../../../../../../apiRPC/card';
import {
  AdditionalFieldsBindings,
  AdditionalFieldsModel,
  getWordsFromCardForField,
  parseAdditionalFieldsFromCard,
} from '../model';
import { agencyDictionary, clientDictionary } from '../dictionaries/model';
import { $additionalFieldsMigrationUiPatch } from '../additionalFieldsMigration.model';
import { PeriodErrors, validatePeriod } from '../utils/validatePeriod';

type UseAdditionalFieldsStateResult = {
  values: AdditionalFieldsModel;
  setValues: Dispatch<SetStateAction<AdditionalFieldsModel>>;
  initialValues: AdditionalFieldsModel;
  initialBindings: AdditionalFieldsBindings;
  parsedMainDynamicObjectId: string | null;
  parsedLegacyDynamicObjectIds: string[];
  mainDynamicObjectId: string | null;
  setMainDynamicObjectId: Dispatch<SetStateAction<string | null>>;
  isAdditionalFieldsExpanded: boolean;
  setIsAdditionalFieldsExpanded: Dispatch<SetStateAction<boolean>>;
  isDynamicObjectCreating: boolean;
  setIsDynamicObjectCreating: Dispatch<SetStateAction<boolean>>;
  periodErrors: PeriodErrors;
  setPeriodErrors: Dispatch<SetStateAction<PeriodErrors>>;
  isFieldsDisabled: boolean;
  canPersistDerived: boolean;
};

export function useAdditionalFieldsState(
  card: RCardGet,
): UseAdditionalFieldsStateResult {
  const migrationUiPatch = useUnit($additionalFieldsMigrationUiPatch);
  const { dynamicObjects } = card;

  const {
    initialValues,
    initialBindings,
    mainDynamicObjectId: parsedMainDynamicObjectId,
    legacyDynamicObjectIds: parsedLegacyDynamicObjectIds,
  } = useMemo(() => {
    const parsed = parseAdditionalFieldsFromCard({ dynamicObjects });
    const values: AdditionalFieldsModel = {
      ...parsed.values,
    };

    return {
      initialValues: values,
      initialBindings: parsed.bindings,
      mainDynamicObjectId: parsed.mainDynamicObjectId ?? null,
      legacyDynamicObjectIds: parsed.legacyDynamicObjectIds,
    };
  }, [dynamicObjects]);

  const [values, setValues] = useState<AdditionalFieldsModel>(initialValues);
  const [mainDynamicObjectId, setMainDynamicObjectId] = useState<string | null>(
    parsedMainDynamicObjectId,
  );
  const [isAdditionalFieldsExpanded, setIsAdditionalFieldsExpanded] =
    useState(false);
  const [isDynamicObjectCreating, setIsDynamicObjectCreating] = useState(false);
  const [periodErrors, setPeriodErrors] = useState<PeriodErrors>({});

  useEffect(() => {
    setValues(initialValues);
    setMainDynamicObjectId(parsedMainDynamicObjectId);
  }, [
    card.uuid,
    initialValues,
    initialBindings,
    parsedMainDynamicObjectId,
    parsedLegacyDynamicObjectIds,
  ]);

  useEffect(() => {
    if (!migrationUiPatch || migrationUiPatch.cardUuid !== card.uuid) {
      return;
    }
    setMainDynamicObjectId(migrationUiPatch.mainDynamicObjectId);
  }, [migrationUiPatch, card.uuid]);

  useEffect(() => {
    const errors = validatePeriod(values.periodStart, values.periodEnd);
    setPeriodErrors(errors);
  }, [values.periodStart, values.periodEnd]);

  useEffect(() => {
    agencyDictionary.initDictionary();
    clientDictionary.initDictionary();
    if (card) {
      agencyDictionary.seedKnownWords(
        getWordsFromCardForField(card, 'agencyName'),
      );
      clientDictionary.seedKnownWords(
        getWordsFromCardForField(card, 'clientName'),
      );
    }
    return () => {
      agencyDictionary.reset();
      clientDictionary.reset();
    };
  }, [card.uuid]);

  const canPersistDerived = Boolean(
    migrationUiPatch &&
      migrationUiPatch.cardUuid === card.uuid &&
      mainDynamicObjectId === migrationUiPatch.mainDynamicObjectId,
  );

  const isFieldsDisabled = !mainDynamicObjectId || isDynamicObjectCreating;

  return {
    values,
    setValues,
    initialValues,
    initialBindings,
    parsedMainDynamicObjectId,
    parsedLegacyDynamicObjectIds,
    mainDynamicObjectId,
    setMainDynamicObjectId,
    isAdditionalFieldsExpanded,
    setIsAdditionalFieldsExpanded,
    isDynamicObjectCreating,
    setIsDynamicObjectCreating,
    periodErrors,
    setPeriodErrors,
    isFieldsDisabled,
    canPersistDerived,
  };
}
